import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { calculateDistance, formatDistance, extractLocality } from "@/lib/distance";
import { formatShiftDateLabel, shiftUrgencyPill } from "@/lib/shift-date-label";
import { ChevronRight, Clock, MapPin, Zap } from "lucide-react";

interface JobListItemProps {
  job: any;
  isSelected: boolean;
  onClick: () => void;
  userLocation?: {
    latitude: number;
    longitude: number;
  } | null;
}

export default function JobListItem({ job, isSelected, onClick, userLocation }: JobListItemProps) {
  const urgency = shiftUrgencyPill(job.shift_date);
  const distance = userLocation && job.hospital_profiles?.latitude && job.hospital_profiles?.longitude
    ? calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        job.hospital_profiles.latitude,
        job.hospital_profiles.longitude
      )
    : null;

  const getLocationDisplay = () => {
    if (distance !== null) {
      return formatDistance(distance);
    }
    if (job.hospital_profiles?.address) {
      return extractLocality(job.hospital_profiles.address);
    }
    return job.hospital_profiles?.city || "Location not set";
  };

  const rateLabel =
    job.compensation != null && job.compensation > 0
      ? `₹${job.compensation.toLocaleString("en-IN")}/hr`
      : "Rate: on request";

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "px-4 py-3.5 lg:px-5 lg:py-4 border-b border-border cursor-pointer transition-all duration-200",
        isSelected
          ? "bg-primary/5 border-l-4 border-l-primary"
          : "hover:bg-muted/50 border-l-4 border-l-transparent"
      )}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className={cn(
                "text-[15px] lg:text-[17px] font-semibold line-clamp-2 leading-snug",
                isSelected ? "text-primary" : "text-foreground"
              )}>
                {job.title}
              </h3>
              <div className="mt-1 flex items-center gap-2 min-w-0 flex-wrap">
                {job.hospital_profiles ? (
                  <span className="text-sm lg:text-base text-primary font-medium truncate">
                    {job.hospital_profiles.hospital_name || "Hospital"}
                  </span>
                ) : (
                  <span className="text-sm lg:text-base text-foreground/80 font-medium truncate">
                    Hospital
                  </span>
                )}
                {job.hospital_profiles?.is_verified && (
                  <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-success/10 text-success flex-shrink-0">
                    Verified
                  </span>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 text-right">
              <div className="text-sm lg:text-base font-semibold text-foreground whitespace-nowrap">
                {rateLabel}
              </div>
              <div className="text-xs lg:text-sm text-muted-foreground whitespace-nowrap flex items-center justify-end gap-1.5">
                {urgency && (
                  <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                    {urgency}
                  </span>
                )}
                <span>{formatShiftDateLabel(job.shift_date)}</span>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm lg:text-[15px]">
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-muted-foreground flex-shrink-0" />
              <span className={cn("truncate", distance !== null ? "text-primary font-medium" : "text-muted-foreground")}>
                {getLocationDisplay()}
              </span>
              {distance !== null && distance < 10 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary flex-shrink-0">
                  <Zap className="h-2.5 w-2.5" />
                  Nearby
                </span>
              )}
            </div>
            <span className="text-muted-foreground/60">•</span>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {job.shift_start_time.slice(0, 5)} – {job.shift_end_time.slice(0, 5)}
              </span>
            </div>
          </div>
        </div>

        <ChevronRight className="h-4 w-4 text-muted-foreground/35 flex-shrink-0 lg:hidden" />
      </div>
    </div>
  );
}
